import React, { useEffect, useState } from "react";
import { Form, Input, Button, DatePicker, Row, Col, Select, Spin, Radio } from "antd";
import { ReactSVG } from "react-svg";
import { useSelector, useDispatch } from "react-redux";

import { DATE_FORMAT_FRONTEND, DATE_FORMAT_QUERY, STATUS, ROUTE_PLAN_TYPIES } from "../../../constants";
import { getTranslation } from "../../../utils/helper";
import LabelComponent from "../../Elements/LabelComponent";
import { doUpdateSettingNoFetching } from "../../../modules/setting/stores/thunk";
import dayjs from "dayjs";

const { Option } = Select;

const OrdersFilterForm = ({ isFetching, query, onSubmit, routePlanType }) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const setting = useSelector(state => state.setting.setting) || user?.company;
  const roles = user?.roles || [];

  const [form] = Form.useForm();
  const [labelIds, setLabelIds] = useState([]);
  const [routePlanTypeForm, setRoutePlanTypeForm] = useState();

  const routePlanTypeField = "routePlanType";
  const routePlanTypeValue = query[routePlanTypeField];

  const orderCodeField = "orderCode";
  const orderCodeValue = query[orderCodeField];

  const statusField = "status";
  const statusValue = query[statusField];

  const deliveryDateField = "deliveryDate";
  const deliveryDateValue = query[deliveryDateField];

  const pickupNameField = "pickupName";
  const pickupNameValue = query[pickupNameField];

  const pickupAddressField = "pickupAddress";
  const pickupAddressValue = query[pickupAddressField];

  const pickupPhoneField = "pickupPhone";
  const pickupPhoneValue = query[pickupPhoneField];

  const deliveryNameField = "deliveryName";
  const deliveryNameValue = query[deliveryNameField];

  const deliveryAddressField = "deliveryAddress";
  const deliveryAddressValue = query[deliveryAddressField];

  const deliveryPhoneField = "deliveryPhone";
  const deliveryPhoneValue = query[deliveryPhoneField];

  const labelsField = "labels";
  const labelsValue = query[labelsField];

  const companyEmailField = "companyEmail";
  const companyEmailValue = query[companyEmailField];

  useEffect(() => {
    form.setFieldsValue(initialValues());
  }, [query]);

  const initialValues = () => {
    const values = {};
    values[routePlanTypeField] = routePlanTypeValue || routePlanType;
    values[orderCodeField] = orderCodeValue;
    values[statusField] = statusValue;
    values[deliveryDateField] = deliveryDateValue && dayjs(deliveryDateValue, DATE_FORMAT_QUERY);
    values[pickupNameField] = pickupNameValue;
    values[pickupAddressField] = pickupAddressValue;
    values[pickupPhoneField] = pickupPhoneValue;
    values[deliveryNameField] = deliveryNameValue;
    values[deliveryAddressField] = deliveryAddressValue;
    values[deliveryPhoneField] = deliveryPhoneValue;
    values[labelsField] = labelsValue;
    if (roles.includes("admin")) values[companyEmailField] = companyEmailValue;
    return values;
  };

  const handleSubmit = values => {
    const params = {};
    params[routePlanTypeField] = values[routePlanTypeField] || "PDP";
    params[orderCodeField] = values[orderCodeField] || null;
    params[statusField] = values[statusField] || null;
    params[deliveryDateField] =
      (values[deliveryDateField] && values[deliveryDateField].format(DATE_FORMAT_QUERY)) || null;
    params[pickupNameField] = values[pickupNameField] || null;
    params[pickupAddressField] = values[pickupAddressField] || null;
    params[pickupPhoneField] = values[pickupPhoneField]?.replace("+", "") || null;
    params[deliveryNameField] = values[deliveryNameField] || null;
    params[deliveryAddressField] = values[deliveryAddressField] || null;
    params[deliveryPhoneField] = values[deliveryPhoneField] || null;
    params[labelsField] = labelIds || null;
    if (roles.includes("admin")) params[companyEmailField] = values[companyEmailField] || null;
    dispatch(doUpdateSettingNoFetching({ routePlanType: params[routePlanTypeField] }));
    onSubmit({ ...query, ...params, currentPage: 1 }, true);
  };

  const handleResetButton = () => {
    onSubmit({}, true);
    form.resetFields();
  };

  return (
    <Spin spinning={isFetching}>
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={initialValues}
        className="FilterForm OrdersFilterForm"
        layout="vertical">
        <div className="FilterFormContent">
          <Form.Item label={getTranslation(setting, "Orders", "Type")} name={routePlanTypeField}>
            <Radio.Group className="RoutePlanType" onChange={e => setRoutePlanTypeForm(e.target.value)}>
              {ROUTE_PLAN_TYPIES.map(item => (
                <Radio key={item.value} value={item.value}>
                  {getTranslation(setting, "Route Plans", item.name)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Row gutter={16}>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Order ID")} name={orderCodeField}>
                <Input size="large" placeholder={getTranslation(setting, "Orders", "Order ID")} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Status")} name={statusField}>
                <Select
                  size="large"
                  placeholder={getTranslation(setting, "Orders", "Status")}
                  showArrow
                  style={{ width: "100%" }}>
                  {STATUS.map((status, index) => (
                    <Option key={index} value={status.code}>
                      {getTranslation(setting, "Orders", status.code)}
                    </Option>
                  ))}
                  ;
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Delivery Date")} name={deliveryDateField}>
                <DatePicker
                  size="large"
                  format={DATE_FORMAT_FRONTEND}
                  placeholder={getTranslation(setting, "Orders", "Delivery Date")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            {routePlanTypeForm === "PDP" && (
              <>
                <Col xs={{ span: 24 }} md={{ span: 8 }}>
                  <Form.Item label={getTranslation(setting, "Orders", "Pickup Name")} name={pickupNameField}>
                    <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Name")} />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 8 }}>
                  <Form.Item label={getTranslation(setting, "Orders", "Pickup Address")} name={pickupAddressField}>
                    <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Address")} />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 8 }}>
                  <Form.Item label={getTranslation(setting, "Orders", "Pickup Phone")} name={pickupPhoneField}>
                    <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Phone")} />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Delivery Name")} name={deliveryNameField}>
                <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Name")} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Delivery Address")} name={deliveryAddressField}>
                <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Address")} />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <Form.Item label={getTranslation(setting, "Orders", "Delivery Phone")} name={deliveryPhoneField}>
                <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Phone")} />
              </Form.Item>
            </Col>
            {roles.includes("admin") && (
              <Col xs={{ span: 24 }} md={{ span: 8 }}>
                <Form.Item label="Company Admin Email" name={companyEmailField}>
                  <Input size="large" placeholder="Company Admin Email" />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Form.Item label={getTranslation(setting, "Orders", "Labels")} name={labelsField}>
            <LabelComponent labelIds={labelIds} setLabelIds={setLabelIds} canEdit={false} />
          </Form.Item>
        </div>
        <div className="FilterFormActions">
          <Button size="large" type="text" onClick={handleResetButton}>
            {getTranslation(setting, "Common", "Reset")}
            <ReactSVG className="icon" src="/icons/reset.svg" />
          </Button>
          <Button size="large" type="primary" htmlType="submit">
            {getTranslation(setting, "Common", "Apply")}
            <ReactSVG className="icon" src="/icons/apply.svg" />
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

export default OrdersFilterForm;
